import { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { ingredientsObjectSelector } from '../app/commonSelector';
import { FETCH_FILTERED_RECIPES, FETCH_RATINGS_SUMMARY_LAST_60_DAYS, GET_ALL_INGREDIENTS } from '../app/graphql/queries';
import { setIngredientsList } from '../app/homepage/homepage.slice';
import { CategoryRecipeID } from '../app/homepage/homepage.types';
import { userSelector } from '../app/main/mainSlice';
import { RatingsInterface, SearchFilteredRecipesInterface } from '../app/search/search.types';
import { useAppDispatch, useAppSelector } from '../app/store/hooks';
import { ErrorManagementContext } from '../utils/errorManagement/ErrorManagementProvider';
import { UrlFilters } from './useFilterRecipe.types';
import { andVariableGenerator, filtersFromQuery, generateHeading, sanitizeListView } from './utils/useFIlterRecipe.utils';

const getCategoryName = (categoryToRecipeID: CategoryRecipeID, filters: UrlFilters) => {
  const categoryId = filters?.Category_ID[0];
  return categoryToRecipeID[categoryId]?.categoryName ?? '';
};

const getCategoryRecipes = (categoryToRecipeID: CategoryRecipeID, filters: UrlFilters, recipesList: Array<SearchFilteredRecipesInterface>) => {
  const categoryId = filters?.Category_ID[0];
  const recipeIds = categoryToRecipeID[categoryId]?.recipeIds;

  return recipeIds
    ? recipeIds
      .map((id: string) => recipesList.find((recipe: SearchFilteredRecipesInterface) => recipe.id === id))
      .filter((recipe): recipe is SearchFilteredRecipesInterface => !!recipe)
    : [];
};

const mergeRatingsWithRecipes = (
  recipes: SearchFilteredRecipesInterface[],
  ratingsHashMap: Record<string, { rating: number | null, ratingCount: number | null }>
) =>
  recipes.map((recipe) => {
    const { rating, ratingCount } = ratingsHashMap[recipe.id] || { rating: null, ratingCount: null };
    return {
      ...recipe,
      metadata: {
        ...recipe.metadata,
        rating,
        ratingCount,
      },
    };
  });

const useFilterRecipe = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const categoryToRecipeID = useAppSelector((state) => state.homepage.categoryToRecipeId);
  const user = useAppSelector(userSelector);
  const ingredients = useAppSelector((state) => state.homepage.ingredients);
  const dispatch = useAppDispatch();
  const filters = filtersFromQuery(query);

  const isSourceCategory = query.get('source') === "category";

  const variables = { filter_conditions: andVariableGenerator(filters) };
  const { cookClubLog } = useContext(ErrorManagementContext);

  const ingredientsMap = useAppSelector(ingredientsObjectSelector);

  const userDiet = user?.userSettings?.diet ?? "";
  const userAllergens = user?.userSettings?.allergens ?? [];
  const userId = user?.id || null;
  const recipesList = useAppSelector((state) => state.homepage.recipeList);
  const [heading, setHeading] = useState<string | null>(isSourceCategory ? getCategoryName(categoryToRecipeID, filters) : "");
  const [recipes, setRecipes] = useState<Array<SearchFilteredRecipesInterface>>(isSourceCategory ? getCategoryRecipes(categoryToRecipeID, filters, recipesList) : []);

  const { loading: fetchingIngredients } = useQuery(GET_ALL_INGREDIENTS, {
    skip: ingredients.length !== 0 || isSourceCategory,
    onCompleted: (data) => {
      dispatch(setIngredientsList(data?.ingredients));
    }
  });

  const { loading: fetchingRecipes } = useQuery(FETCH_FILTERED_RECIPES, {
    skip: ingredients.length === 0 || isSourceCategory,
    variables,
    onCompleted: (data) => {
      const sanitizedData = sanitizeListView(data, ingredientsMap, userDiet, userAllergens, userId, filters);
      setHeading(generateHeading(query));
      setRecipes(sanitizedData);
    },
    onError: (err) => {
      cookClubLog("", "Unable to fetch Recipes!", '', { ui: { type: "error" }, browserConsole: true });
      console.error('Error fetching the recipes:', err);
    }
  });

  const { loading: fetchingRatings, data: ratingsData } = useQuery(FETCH_RATINGS_SUMMARY_LAST_60_DAYS);

  const ratingsHashMap: Record<string, { rating: number | null, ratingCount: number | null }> = {};
  ratingsData?.ratingsSummaryLast60DaysView.forEach((rating: RatingsInterface) => {
    ratingsHashMap[rating.recipeID] = {
      rating: rating.rating || null,
      ratingCount: rating.ratingCount || null,
    };
  });
  const finalRecipes = mergeRatingsWithRecipes(recipes, ratingsHashMap);

  return { recipes: finalRecipes, filters, heading, loading: fetchingRecipes || fetchingRatings || fetchingIngredients };
};

export default useFilterRecipe;